import { toneChipClasses, toneTextClasses } from '@shared/utils/statusTone';
import { useImpersonation } from '@shared/contexts/ImpersonationContext';

/**
 * Feature 711: Read-Only Mode badge for impersonation.
 *
 * This component displays a small badge indicating that the current page
 * is in read-only mode during impersonation. It should be placed near
 * page titles on sensitive pages (settings, billing, team pages).
 *
 * @example
 * ```tsx
 * import ReadOnlyBadge from '@shared/components/ReadOnlyBadge';
 *
 * export default function Settings() {
 *   return (
 *     <div>
 *       <div className="flex items-center gap-3">
 *         <h1 className="text-3xl font-bold">Settings</h1>
 *         <ReadOnlyBadge />
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export default function ReadOnlyBadge() {
  const { isImpersonating } = useImpersonation();

  // Only show during impersonation
  if (!isImpersonating) {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${toneChipClasses.info}`}
      title="You are viewing this page in read-only mode while impersonating"
    >
      <svg
        className={`mr-1.5 h-4 w-4 ${toneTextClasses.info}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
      Read-Only Mode
    </span>
  );
}
